import React, { useState, useEffect } from 'react';
import { getAvailableProducts, getInventory } from '../services/inventoryService';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../services/gstCalculations';
import { 
  CubeIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  TagIcon,
  FunnelIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

function Inventory() {
  const { currentUser } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    quarter: '',
    year: '',
    productName: ''
  });

  useEffect(() => {
    if (currentUser) {
      loadInventory();
    }
  }, [currentUser, filters]);

  const loadInventory = async () => {
    try {
      setLoading(true);
      const [allInventory, available] = await Promise.all([
        getInventory(currentUser.uid),
        getAvailableProducts(currentUser.uid)
      ]);
      
      setInventory(allInventory);
      setAvailableProducts(available);
    } catch (error) {
      console.error('Error loading inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const getQuarterAndYear = (date) => {
    const d = new Date(date);
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    
    let quarter;
    if (month >= 4 && month <= 6) quarter = 'Q1';
    else if (month >= 7 && month <= 9) quarter = 'Q2';
    else if (month >= 10 && month <= 12) quarter = 'Q3';
    else quarter = 'Q4';
    
    const financialYear = month >= 4 ? year : year - 1;
    return { quarter, year: financialYear };
  };

  const filteredInventory = inventory.filter(item => {
    const { quarter, year } = getQuarterAndYear(item.purchaseDate);
    const isAvailable = availableProducts.some(avail => avail.imeiNumber === item.imeiNumber);
    
    return (
      (!filters.quarter || quarter === filters.quarter) &&
      (!filters.year || year.toString() === filters.year) &&
      (!filters.productName || item.productName.toLowerCase().includes(filters.productName.toLowerCase())) &&
      isAvailable // Only show available (unsold) products
    );
  });

  const getStatusColor = (item) => {
    const isAvailable = availableProducts.some(avail => avail.imeiNumber === item.imeiNumber);
    return isAvailable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const getStatusText = (item) => {
    const isAvailable = availableProducts.some(avail => avail.imeiNumber === item.imeiNumber);
    return isAvailable ? 'Available' : 'Sold';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Inventory Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Track your unsold products and purchase history
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <div className="bg-blue-50 px-4 py-2 rounded-lg">
            <div className="text-sm text-blue-600 font-medium">
              Available: {availableProducts.length} products
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CubeIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Products
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {inventory.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TagIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Available
                  </dt>
                  <dd className="text-lg font-medium text-green-600">
                    {availableProducts.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Value
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(availableProducts.reduce((sum, item) => sum + (item.purchaseAmount || 0), 0))}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-6 w-6 text-purple-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Current Quarter
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {(() => {
                      const now = new Date();
                      const { quarter, year } = getQuarterAndYear(now);
                      return `${quarter} ${year}-${(year + 1).toString().substr(-2)}`;
                    })()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quarter</label>
            <select
              value={filters.quarter}
              onChange={(e) => setFilters({ ...filters, quarter: e.target.value })}
              className="input"
            >
              <option value="">All Quarters</option>
              <option value="Q1">Q1 (Apr-Jun)</option>
              <option value="Q2">Q2 (Jul-Sep)</option>
              <option value="Q3">Q3 (Oct-Dec)</option>
              <option value="Q4">Q4 (Jan-Mar)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              className="input"
            >
              <option value="">All Years</option>
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - 2 + i;
                return (
                  <option key={year} value={year}>{year}-{(year + 1).toString().substr(-2)}</option>
                );
              })}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
            <input
              type="text"
              value={filters.productName}
              onChange={(e) => setFilters({ ...filters, productName: e.target.value })}
              className="input"
              placeholder="Search products..."
            />
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ quarter: '', year: '', productName: '' })}
              className="btn-secondary w-full"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Product Inventory</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            {filteredInventory.length} product{filteredInventory.length !== 1 ? 's' : ''} found
          </p>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : filteredInventory.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th>Product Name</th>
                  <th>IMEI Number</th>
                  <th>Purchase Date</th>
                  <th>Quarter</th>
                  <th>Purchase Amount</th>
                  <th>Supplier</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {filteredInventory.map((item) => {
                  const { quarter, year } = getQuarterAndYear(item.purchaseDate);
                  return (
                    <tr key={item.id}>
                      <td className="font-medium">{item.productName}</td>
                      <td className="font-mono text-sm">{item.imeiNumber}</td>
                      <td>
                        {item.purchaseDate
                          ? new Date(item.purchaseDate).toLocaleDateString('en-IN')
                          : 'N/A'
                        }
                      </td>
                      <td>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {quarter} {year}-{(year + 1).toString().substr(-2)}
                        </span>
                      </td>
                      <td className="font-medium">{formatCurrency(item.purchaseAmount || 0)}</td>
                      <td>{item.supplierName || 'N/A'}</td>
                      <td>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item)}`}>
                          {getStatusText(item)}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <button
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No products found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filters.quarter || filters.year || filters.productName
                ? 'Try adjusting your filters to see more products.'
                : 'Add some products through purchases to see them here.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Inventory;
